import { Link } from 'react-router-dom'
import { useAuth } from '@/stores/authStore'
import {
  LogOut,
  Briefcase,
  Search,
  Menu,
  X,
  Sparkles,
  ChevronDown,
  CreditCard,
  Settings,
  FileText,
  User,
  Globe,
  History,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { useLanguage, type Language } from '@/stores/languageStore'
import { useJobOfferNotifications } from '@/hooks/useJobOfferNotifications'

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false)
  const { t } = useTranslation(['common', 'auth'])
  const { unseenCount } = useJobOfferNotifications()
  const { language, setLanguage } = useLanguage()
  const LANGUAGES: { code: Language; label: string }[] = [
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
    { code: 'ar', label: 'AR' },
  ]

  // Determine dashboard link based on user role
  const getDashboardLink = () => {
    if (!isAuthenticated || !user) return '/'
    return user.role === 'RECRUITER' ? '/recruiter/dashboard' : '/candidate/dashboard'
  }

  return (
    <header className="bg-white/80 border-b border-gray-200 sticky top-0 z-50 backdrop-blur-md">
      <nav className="container-custom py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to={getDashboardLink()}
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <span className="text-2xl font-extrabold tracking-tight text-gray-900">headhunter</span>
            <span className="text-2xl font-extrabold tracking-tight text-blue-600">.tn</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {isAuthenticated ? (
              <>
                {/* Nav links — left of separator */}
                {user?.role === 'CANDIDATE' && (
                  <>
                    <Link
                      to="/candidate/job-offers"
                      className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors relative"
                    >
                      <FileText size={20} />
                      <span className="font-medium">{t('nav.jobOffers')}</span>
                      {unseenCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-5 min-w-5 flex items-center justify-center px-1">
                          {unseenCount > 99 ? '99+' : unseenCount}
                        </span>
                      )}
                    </Link>
                    <Link
                      to="/candidate/profile/edit"
                      className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      <Briefcase size={20} />
                      <span className="font-medium">{t('nav.profile')}</span>
                    </Link>
                  </>
                )}

                {user?.role === 'RECRUITER' && (
                  <>
                    <Link
                      to="/recruiter/search"
                      className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      <Search size={20} />
                      <span className="font-medium">{t('nav.search')}</span>
                    </Link>

                    {/* AI Match - Prominent Button */}
                    <Link
                      to="/recruiter/ai-matching"
                      className="flex items-center gap-2 px-3 xl:px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
                    >
                      <Sparkles size={18} />
                      <span className="font-semibold text-sm xl:text-base">{t('nav.aiMatch')}</span>
                    </Link>

                    <Link
                      to="/recruiter/jobs"
                      className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      <Briefcase size={20} />
                      <span className="font-medium">{t('nav.jobs')}</span>
                    </Link>

                    <Link
                      to="/recruiter/job-offers"
                      className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors relative"
                    >
                      <FileText size={20} />
                      <span className="font-medium">{t('nav.offers')}</span>
                      {unseenCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full h-5 min-w-5 flex items-center justify-center px-1">
                          {unseenCount > 99 ? '99+' : unseenCount}
                        </span>
                      )}
                    </Link>

                    <Link
                      to="/recruiter/contacts"
                      className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      <Briefcase size={20} />
                      <span className="font-medium">{t('nav.contacts')}</span>
                    </Link>
                  </>
                )}

                {/* Separator */}
                <div className="h-6 w-px bg-gray-200"></div>

                {/* Account menu — always right of separator */}
                {user?.role === 'RECRUITER' && (
                  <div className="relative">
                    <button
                      onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                      className="flex items-center gap-1 xl:gap-2 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      {user?.profilePhoto ? (
                        <img
                          src={user.profilePhoto}
                          alt={user.firstName || 'User'}
                          className="w-7 h-7 rounded-full rounded-avatar border border-gray-200 object-cover"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full rounded-avatar bg-gray-100 flex items-center justify-center border border-gray-200">
                          <User size={14} className="text-gray-500" />
                        </div>
                      )}
                      <span className="font-medium text-sm xl:text-base">
                        {user?.firstName || user?.email}
                      </span>
                      <ChevronDown
                        size={14}
                        className={`transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {isAccountMenuOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setIsAccountMenuOpen(false)}
                        />
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                          <div className="px-3 py-2 flex items-center gap-1 border-b border-gray-100">
                            <Globe size={14} className="text-gray-400 mr-1 flex-shrink-0" />
                            {LANGUAGES.map(({ code, label }) => (
                              <button
                                key={code}
                                onClick={() => setLanguage(code)}
                                className={`text-xs font-medium px-2 py-1 rounded ${language === code ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <Link
                            to="/recruiter/packs"
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setIsAccountMenuOpen(false)}
                          >
                            <CreditCard size={18} />
                            <span>{t('nav.subscription')}</span>
                          </Link>
                          <Link
                            to="/recruiter/pack-history"
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setIsAccountMenuOpen(false)}
                          >
                            <History size={18} />
                            <span>{t('nav.packHistory')}</span>
                          </Link>
                          <Link
                            to="/recruiter/profile/edit"
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setIsAccountMenuOpen(false)}
                          >
                            <Settings size={18} />
                            <span>{t('nav.profile')}</span>
                          </Link>
                          <div className="border-t border-gray-100 my-1" />
                          <button
                            onClick={() => {
                              logout()
                              setIsAccountMenuOpen(false)
                            }}
                            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors w-full"
                          >
                            <LogOut size={18} />
                            <span>{t('button.signOut')}</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {user?.role === 'CANDIDATE' && (
                  <div className="relative">
                    <button
                      onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                      className="flex items-center gap-1 xl:gap-2 text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      {user?.profilePhoto ? (
                        <img
                          src={user.profilePhoto}
                          alt={user.firstName || 'User'}
                          className="w-7 h-7 rounded-full rounded-avatar border border-gray-200 object-cover"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full rounded-avatar bg-gray-100 flex items-center justify-center border border-gray-200">
                          <User size={14} className="text-gray-500" />
                        </div>
                      )}
                      <span className="font-medium text-sm xl:text-base">
                        {user?.firstName || user?.email}
                      </span>
                      <ChevronDown
                        size={14}
                        className={`transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {isAccountMenuOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setIsAccountMenuOpen(false)}
                        />
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                          <div className="px-3 py-2 flex items-center gap-1 border-b border-gray-100">
                            <Globe size={14} className="text-gray-400 mr-1 flex-shrink-0" />
                            {LANGUAGES.map(({ code, label }) => (
                              <button
                                key={code}
                                onClick={() => setLanguage(code)}
                                className={`text-xs font-medium px-2 py-1 rounded ${language === code ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={() => {
                              logout()
                              setIsAccountMenuOpen(false)
                            }}
                            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors w-full"
                          >
                            <LogOut size={18} />
                            <span>{t('button.signOut')}</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <LanguageSwitcher />
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-gray-900 font-medium transition-colors border border-gray-200 hover:border-gray-300 px-5 py-2 rounded-lg"
                >
                  {t('auth:login.title')}
                </Link>
                <Link
                  to="/register"
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 hover:border-blue-300 px-5 py-2 rounded-lg font-medium transition-colors"
                >
                  {t('auth:register.title')}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 bg-white rounded-lg border border-gray-200 shadow-sm p-4 animate-slide-down">
            <div className="flex flex-col gap-4">
              <LanguageSwitcher />

              <div className="h-px bg-gray-200"></div>

              {isAuthenticated ? (
                <>
                  {user?.role === 'CANDIDATE' && (
                    <>
                      <Link
                        to="/candidate/job-offers"
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 py-2 relative"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <FileText size={20} />
                        <span className="font-medium">{t('nav.jobOffers')}</span>
                        {unseenCount > 0 && (
                          <span className="bg-red-600 text-white text-xs font-bold rounded-full h-5 min-w-5 flex items-center justify-center px-1 ml-auto">
                            {unseenCount > 99 ? '99+' : unseenCount}
                          </span>
                        )}
                      </Link>
                      <Link
                        to="/candidate/profile/edit"
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 py-2"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Briefcase size={20} />
                        <span className="font-medium">{t('nav.profile')}</span>
                      </Link>
                    </>
                  )}

                  {user?.role === 'RECRUITER' && (
                    <>
                      {/* Nav links */}
                      <Link
                        to="/recruiter/search"
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 py-2"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Search size={20} />
                        <span className="font-medium">{t('nav.search')}</span>
                      </Link>

                      {/* AI Match - Prominent */}
                      <Link
                        to="/recruiter/ai-matching"
                        className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Sparkles size={20} />
                        <span className="font-semibold">{t('nav.aiMatch')}</span>
                      </Link>

                      <Link
                        to="/recruiter/jobs"
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 py-2"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Briefcase size={20} />
                        <span className="font-medium">{t('nav.jobs')}</span>
                      </Link>

                      <Link
                        to="/recruiter/job-offers"
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 py-2 relative"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <FileText size={20} />
                        <span className="font-medium">{t('nav.offers')}</span>
                        {unseenCount > 0 && (
                          <span className="bg-red-600 text-white text-xs font-bold rounded-full h-5 min-w-5 flex items-center justify-center px-1 ml-auto">
                            {unseenCount > 99 ? '99+' : unseenCount}
                          </span>
                        )}
                      </Link>

                      <Link
                        to="/recruiter/contacts"
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 py-2"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Briefcase size={20} />
                        <span className="font-medium">{t('nav.contacts')}</span>
                      </Link>

                      <div className="h-px bg-gray-200 my-2"></div>

                      {/* Account section */}
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 truncate">
                        {user?.firstName || user?.email}
                      </div>
                      <Link
                        to="/recruiter/packs"
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 py-2 pl-2"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <CreditCard size={20} />
                        <span className="font-medium">{t('nav.subscription')}</span>
                      </Link>
                      <Link
                        to="/recruiter/pack-history"
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 py-2 pl-2"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <History size={20} />
                        <span className="font-medium">{t('nav.packHistory')}</span>
                      </Link>
                      <Link
                        to="/recruiter/profile/edit"
                        className="flex items-center gap-2 text-gray-600 hover:text-blue-600 py-2 pl-2"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Settings size={20} />
                        <span className="font-medium">{t('nav.profile')}</span>
                      </Link>
                    </>
                  )}

                  <div className="h-px bg-gray-200 my-4"></div>

                  <button
                    onClick={() => {
                      logout()
                      setIsMobileMenuOpen(false)
                    }}
                    className="flex items-center gap-2 text-red-600 hover:text-red-700 py-2"
                  >
                    <LogOut size={20} />
                    <span className="font-medium">{t('button.signOut')}</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-600 hover:text-gray-900 font-medium py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('auth:login.title')}
                  </Link>
                  <Link
                    to="/register"
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-center py-2 px-4 rounded-lg font-medium transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('auth:register.title')}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}
